import React from 'react';
import { useQuery } from '@tanstack/react-query';

import { TextFieldProps } from "@mui/material/TextField";
import {
	Chip,
	Stack,
	TextField,
	Autocomplete,
	AutocompleteProps,
} from "@mui/material";

import { FindManyFilter } from "@onair/types/extends";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Status, Category, CategoryType, restRepository, GetAllCategoryEntity } from '@onair/repositories';

export type AutocompleteBaseProps = Omit<
	AutocompleteProps<any, boolean, boolean, boolean>,
	"renderInput" | "options"
>;

interface Value {
	label: string;
	value: string;
}

const LIMIT = 100;

export type PropTypes = AutocompleteBaseProps & {
	value: Value;
	onChange: (v: Value) => void;
	label?: string;
	businessSlugs?: string[];
	placeholder?: string;
	multiple?: boolean;
	helperText?: React.ReactNode;
	textFieldProps?: TextFieldProps;
	type: CategoryType[];
	error?: {
		message: string;
	};
};

const getCategories = async (filter: { businessSlugs?: string[], type: CategoryType[] }) => {
	const queryObject: FindManyFilter<Category> = {
		where: {},
		select: {
			id: true,
			name: true,
		},
	};

	if (filter.businessSlugs && filter.businessSlugs.length > 0) {
		queryObject.where = {
			...queryObject.where,
			// isRoot: true,
			isDeleted: false,
			status: Status.ACTIVE,
			OR: [
				{
					isRoot: true,
					ancestors: {
						every: {
							descendant: {
								OR: [
									{
										status: Status.DISABLED,
									},
									{
										isDeleted: true
									}
								]
							}
						}
					}
				},
				{
					isRoot: false
				}
			],
			...(filter.type.length > 0 && {
				type: {
					hasSome: filter.type
				}
			}),
			business: {
				some: {
					slug: {
						in: filter.businessSlugs,
					},
				},
			},
		};
		queryObject.select = {
			...queryObject.select,
			descendants: {
				select: {
					ancestor: {
						select: {
							id: true,
							name: true,
						}
					},
				},
			},
			business: true,
		};
	} else {
		queryObject.where = {
			...queryObject.where,
			isRoot: true,
			isDeleted: false,
			status: Status.ACTIVE,
			...(filter.type.length > 0 && {
				type: {
					hasSome: filter.type
				}
			}),
		};
	}

	if (filter.type.includes(CategoryType.ONLINE_COURSE)) {
		queryObject.where = {
			...queryObject.where,
			isRoot: true,
		}
		queryObject.select = {
			...queryObject.select,
			ancestors: {
				select: {
					descendant: {
						select: {
							id: true,
							name: true,
						}
					}
				}
			}
		}
	}

	const data = await restRepository.api.categoryGetAll({ filter: JSON.stringify(queryObject), limit: LIMIT })
	return data;
};

export const SelectCategoriesV2: React.FC<PropTypes> = (props) => {
	const {
		multiple = true,
		textFieldProps,
		label,
		businessSlugs,
		onChange,
		value,
		placeholder,
		helperText,
		type,
		error,
		...rest
	} = props;

	const { isLoading, data: categories } = useQuery({
		queryKey: ["categories", "select", businessSlugs, type],
		queryFn: () => getCategories({ businessSlugs, type }),
		staleTime: 1 * 60 * 1000,
	});
	return (
		<Autocomplete
			freeSolo={false}
			limitTags={3}
			{...rest}
			options={
				(categories?.data?.items as GetAllCategoryEntity[])?.map((el) => {
					if (el.ancestors && el.ancestors.length > 0) {
						return el.ancestors.map((ancestor) => ({
							value: ancestor.descendant?.id ?? '',
							label: ancestor.descendant?.name ?? '',
							group: el.name,
						}));
					};
					return ({
						value: el.id,
						label: el.name,
						isRoot: el.isRoot,
						businessName:
							el.isRoot || !el.business || el.business.length === 0
								? ''
								: el.business[0]?.name || 'Unknown Business',
						parentName:
							el.isRoot || !el.descendants || el.descendants.length === 0
								? ''
								: el.descendants[0]?.ancestor?.name || 'Unknown parent',
					})
				})?.flat()
					?.sort((a, b) => {
						const groupA = businessSlugs && businessSlugs.length > 0 && !a.isRoot
							? `${a.businessName} / ${a.parentName}`
							: '';
						const groupB = businessSlugs && businessSlugs.length > 0 && !b.isRoot
							? `${b.businessName} / ${b.parentName}`
							: '';
						return groupA.localeCompare(groupB);
					}) ?? []
			}
			groupBy={(option) => (businessSlugs && businessSlugs.length > 0 && !option.isRoot
				? `${option.businessName} ${option.parentName && '/'} ${option.parentName}`
				: option.group ?? '')}
			loading={isLoading}
			filterSelectedOptions
			getOptionLabel={(option) => option.label}
			renderGroup={(params) => (
				<Stack key={params.key}>
					<Stack style={{
						fontWeight: 600,
						color: 'text.disabled',
						padding: '10px',
						marginLeft: '-5px',
						backgroundColor: 'transparent',
					}}
					>
						{params.group}
					</Stack>
					<Stack style={{ padding: 0 }}>
						{params.children}
					</Stack>
				</Stack>
			)}
			isOptionEqualToValue={(option, value) => option?.value === value?.value}
			multiple={multiple}
			autoHighlight={!multiple}
			disableCloseOnSelect={multiple}
			value={value}
			onChange={(e, v) => onChange?.(v)}
			renderInput={(params) => {
				if (textFieldProps?.InputLabelProps?.shrink !== undefined) {
					textFieldProps.InputLabelProps = { shrink: true };
				}

				return (
					<TextField
						{...params}
						label={label}
						helperText={error?.message ?? helperText}
						error={!!error}
						{...textFieldProps}
					/>
				);
			}}
			renderTags={
				multiple
					? (selected, getTagProps) => {
						return selected.map((option, index: number) => {
							const { key, ...tagProps } = getTagProps({
								index,
							});

							return (
								<Chip
									key={key}
									{...tagProps}
									label={option.label}
									// disabled={businessSlugs && businessSlugs.length > 0 && option.isRoot}
									size="small"
									color="info"
									variant="soft"
								/>
							);
						});
					}
					: undefined
			}
		/>
	);
};
